import React, { useState, useEffect, useCallback } from 'react';
import { useProject } from '../contexts/ProjectContext';
import api from '../api';
import toast from 'react-hot-toast';
import SearchableMultiSelect from '../components/SearchableMultiSelect';
import {
  FileText,
  Plus,
  Save,
  Download,
  ChevronDown,
  ChevronUp,
  Trash2,
  Edit2,
  X,
  Loader2,
  AlertCircle,
  Sparkles,
  Building2,
  Users,
  Globe,
  Settings,
  ClipboardList,
  RefreshCw,
  Search,
  Filter,
  Eye,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  GitBranch,
  Shield,
  Check
} from 'lucide-react';

interface MetadataOptions {
  os_level_1?: Array<{ value: string; display_name: string; metadata?: any }>;
  os_level_2?: Array<{ value: string; display_name: string; metadata?: any }>;
  os_level_3?: Array<{ value: string; display_name: string; metadata?: any }>;
  process_taxonomy?: Array<{ value: string; display_name: string }>;
  performing_country?: Array<{ value: string; display_name: string }>;
  performing_legal_entity?: Array<{ value: string; display_name: string }>;
  affected_country?: Array<{ value: string; display_name: string }>;
  affected_legal_entity?: Array<{ value: string; display_name: string }>;
  applications?: Array<{ value: string; display_name: string }>;
  supporting_models?: Array<{ value: string; display_name: string }>;
  third_party_relationship_type?: Array<{ value: string; display_name: string }>;
  product_inventory?: Array<{ value: string; display_name: string }>;
  business_functions?: Array<{ value: string; display_name: string }>;
}

interface RCSAProcessForm {
  id?: number;
  rau_short_name: string;
  row_sequence: number;
  os_level_1: string;
  os_level_2: string;
  os_level_3: string;
  process_taxonomy: string;
  process_name: string;
  process_description: string;
  process_starting_point: string;
  process_ending_point: string;
  process_scope: string[];
  process_owner: string;
  business_unit_control_manager: string[];
  performing_country: string[];
  performing_legal_entity: string[];
  affected_countries: string[];
  affected_legal_entities: string[];
  supporting_applications: string[];
  supporting_models: string[];
  non_model_tools: string;
  external_third_party_support: 'None' | 'Partial' | 'All';
  third_party_relationship_type: string[];
  external_parties_listing: string[];
  alternative_practice_id: string;
  related_products: string[];
  related_bias: string[];
  ai_confidence_score: number;
  ai_generated_fields: Record<string, boolean>;
}

interface ProcessStep {
  id: string;
  name: string;
  description: string;
  order: number;
  risk_level: string;
}

interface RiskAssessment {
  id: string;
  process_step_id: string;
  risk_name: string;
  risk_description: string;
  inherent_likelihood: number;
  inherent_impact: number;
  inherent_risk_score: number;
  control_name: string;
  control_effectiveness: string;
  residual_risk_score: number;
}

const initialFormState: RCSAProcessForm = {
  rau_short_name: '',
  row_sequence: 1,
  os_level_1: '',
  os_level_2: '',
  os_level_3: '',
  process_taxonomy: '',
  process_name: '',
  process_description: '',
  process_starting_point: '',
  process_ending_point: '',
  process_scope: [],
  process_owner: '',
  business_unit_control_manager: [],
  performing_country: [],
  performing_legal_entity: [],
  affected_countries: [],
  affected_legal_entities: [],
  supporting_applications: [],
  supporting_models: [],
  non_model_tools: '',
  external_third_party_support: 'None',
  third_party_relationship_type: [],
  external_parties_listing: [],
  alternative_practice_id: '',
  related_products: [],
  related_bias: [],
  ai_confidence_score: 0,
  ai_generated_fields: {}
};

const WIZARD_STEPS = [
  { id: 1, name: 'Define Process', icon: ClipboardList, description: 'Basic process information' },
  { id: 2, name: 'Save', icon: Save, description: 'Review and save process' }
];

interface DocumentSummary {
  id: number;
  document_name: string;
  summary: string;
  executive_summary?: string;
  business_context?: {
    primary_purpose: string;
    business_areas: string[];
    activities_covered: string[];
  };
  key_points?: {
    key_processes: string[];
  };
}

interface RCSAProcessBuilderProps {
  documentSummaries?: DocumentSummary[];
}

const RCSAProcessBuilder: React.FC<RCSAProcessBuilderProps> = ({ documentSummaries = [] }) => {
  const { currentProject } = useProject();

  const [processes, setProcesses] = useState<RCSAProcessForm[]>([]);
  const [metadata, setMetadata] = useState<MetadataOptions>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [showWizard, setShowWizard] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<RCSAProcessForm>(initialFormState);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const [processSteps, setProcessSteps] = useState<ProcessStep[]>([]);
  const [riskAssessments, setRiskAssessments] = useState<RiskAssessment[]>([]);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [loadingRisks, setLoadingRisks] = useState(false);

  const wizardContentRef = React.useRef<HTMLDivElement>(null);

  const [filteredOsLevel2, setFilteredOsLevel2] = useState<Array<{ value: string; display_name: string }>>([]);
  const [filteredOsLevel3, setFilteredOsLevel3] = useState<Array<{ value: string; display_name: string }>>([]);

  const [textInputs, setTextInputs] = useState({
    business_unit_control_manager: '',
    external_parties_listing: ''
  });

  const fetchMetadata = useCallback(async () => {
    try {
      const response = await api.get('/api/rcsa-process/metadata');
      console.log('[RCSA Metadata] Received:', {
        os_level_1: response.data.os_level_1?.length || 0,
        os_level_2: response.data.os_level_2?.length || 0,
        os_level_3: response.data.os_level_3?.length || 0,
        process_taxonomy: response.data.process_taxonomy?.length || 0,
        performing_country: response.data.performing_country?.length || 0,
        sample_os_level_2: response.data.os_level_2?.[0]
      });
      setMetadata(response.data);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  }, []);

  const fetchProcesses = useCallback(async () => {
    if (!currentProject) return;
    try {
      setLoading(true);
      const response = await api.get(`/api/rcsa-process/project/${currentProject.id}`);
      setProcesses(response.data);
    } catch (error) {
      console.error('Error fetching processes:', error);
      toast.error('Failed to load RCSA processes');
    } finally {
      setLoading(false);
    }
  }, [currentProject]);

  const fetchProcessSteps = useCallback(async () => {
    if (!currentProject) return;
    setLoadingSteps(true);
    try {
      const response = await api.get(`/api/process-maps/project/${currentProject.id}`);
      const maps = response.data?.process_maps || response.data || [];
      const allSteps: ProcessStep[] = [];

      console.log('[RCSA] Fetched process maps:', maps.length);

      maps.forEach((map: any, mapIndex: number) => {
        console.log(`[RCSA] Processing map ${mapIndex + 1}:`, {
          id: map.id,
          level: map.level,
          title: map.title,
          hasMapData: !!map.map_data,
          mapDataType: typeof map.map_data,
          mapDataKeys: map.map_data ? Object.keys(map.map_data) : []
        });

        if (map.map_data) {
          let mapData = map.map_data;

          // Parse if string
          if (typeof mapData === 'string') {
            try {
              mapData = JSON.parse(mapData);
            } catch (e) {
              console.error('[RCSA] Failed to parse map_data:', e);
              return;
            }
          }

          console.log(`[RCSA] Map ${map.id} structure:`, {
            hasElements: !!mapData.elements,
            hasProcessesInElements: !!(mapData.elements?.processes),
            processCountInElements: mapData.elements?.processes?.length || 0,
            hasProcessesAtRoot: !!mapData.processes,
            processCountAtRoot: mapData.processes?.length || 0,
            hasNodes: !!mapData.nodes,
            nodeCount: mapData.nodes?.length || 0
          });

          // Try multiple locations for processes
          let processes: any[] = [];

          if (mapData.elements?.processes && Array.isArray(mapData.elements.processes)) {
            processes = mapData.elements.processes;
            console.log(`[RCSA] Found ${processes.length} processes in elements.processes`);
          } else if (mapData.processes && Array.isArray(mapData.processes)) {
            processes = mapData.processes;
            console.log(`[RCSA] Found ${processes.length} processes in root.processes`);
          } else if (mapData.nodes && Array.isArray(mapData.nodes)) {
            processes = mapData.nodes;
            console.log(`[RCSA] Found ${processes.length} processes in nodes`);
          }

          if (processes.length > 0) {
            console.log(`[RCSA] Sample process structure:`, processes[0]);
          }

          processes.forEach((proc: any, index: number) => {
            const stepName = proc.name || proc.step_name || proc.data?.label || proc.label || `Step ${index + 1}`;
            const stepDesc = proc.detailed_description || proc.description || proc.data?.description || `Process step from ${map.title || 'Process Map'}`;

            allSteps.push({
              id: `${map.id}-step-${proc.step_number || index}`,
              name: stepName,
              description: stepDesc,
              order: allSteps.length + 1,
              risk_level: proc.risk_level || proc.data?.risk_level || 'Medium'
            });
          });
        }
      });

      console.log(`[RCSA] Total process steps extracted: ${allSteps.length}`);
      setProcessSteps(allSteps);
    } catch (error) {
      console.error('Error fetching process steps:', error);
    } finally {
      setLoadingSteps(false);
    }
  }, [currentProject]);

  const fetchRiskAssessments = useCallback(async () => {
    if (!currentProject) return;
    setLoadingRisks(true);
    try {
      const response = await api.get(`/api/risk-mapping/${currentProject.id}`);
      const riskData = response.data?.data?.risk_assessments || response.data?.risk_assessments || [];
      const allRisks: RiskAssessment[] = [];

      riskData.forEach((assessment: any) => {
        allRisks.push({
          id: assessment.id?.toString() || `risk-${allRisks.length}`,
          process_step_id: assessment.process_step_id || '',
          risk_name: assessment.risk_name || assessment.process_step_name || '',
          risk_description: assessment.risk_description || assessment.process_step_description || '',
          inherent_likelihood: assessment.inherent_probability || assessment.inherent_likelihood || 0,
          inherent_impact: assessment.inherent_impact || 0,
          inherent_risk_score: assessment.inherent_risk_score || 0,
          control_name: assessment.recommended_controls?.[0] || assessment.control_name || '',
          control_effectiveness: assessment.control_effectiveness || 'Not Assessed',
          residual_risk_score: assessment.residual_risk_score || assessment.inherent_risk_score || 0
        });
      });

      setRiskAssessments(allRisks);
    } catch (error) {
      console.error('Error fetching risk assessments:', error);
    } finally {
      setLoadingRisks(false);
    }
  }, [currentProject]);

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  useEffect(() => {
    if (currentProject) {
      fetchProcesses();
    }
  }, [currentProject, fetchProcesses]);

  // Fetch process steps and risk assessments when entering Review step
  useEffect(() => {
    if (showWizard && currentStep === 2) {
      fetchProcessSteps();
      fetchRiskAssessments();
    }
  }, [showWizard, currentStep, fetchProcessSteps, fetchRiskAssessments]);

  // Scroll to top when changing steps
  useEffect(() => {
    if (wizardContentRef.current) {
      wizardContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  useEffect(() => {
    if (metadata.os_level_2) {
      if (form.os_level_1) {
        // Get the display name for the selected OS Level 1
        const selectedOsLevel1 = metadata.os_level_1?.find(item => item.value === form.os_level_1);
        const osLevel1DisplayName = selectedOsLevel1?.display_name || '';

        // Try to filter by parent (check both value and display_name)
        const filtered = metadata.os_level_2.filter(item =>
          item.metadata?.parent === form.os_level_1 ||
          item.metadata?.parent_value === form.os_level_1 ||
          item.metadata?.parent === osLevel1DisplayName ||
          item.metadata?.parent_value === osLevel1DisplayName
        );

        console.log('[OS Level 2 Filter]', {
          selectedValue: form.os_level_1,
          displayName: osLevel1DisplayName,
          totalItems: metadata.os_level_2.length,
          filteredCount: filtered.length,
          sampleMetadata: metadata.os_level_2.slice(0, 3).map(i => ({ value: i.value, metadata: i.metadata }))
        });

        // ✅ FIX: Only show filtered items, don't show all if empty
        // Client requirement: L2 should ONLY show items related to selected L1
        setFilteredOsLevel2(filtered);
      } else {
        setFilteredOsLevel2(metadata.os_level_2);
      }
    } else {
      setFilteredOsLevel2([]);
    }
  }, [form.os_level_1, metadata.os_level_1, metadata.os_level_2]);

  useEffect(() => {
    if (metadata.os_level_3) {
      if (form.os_level_2) {
        // Get the display name for the selected OS Level 2
        const selectedOsLevel2 = metadata.os_level_2?.find(item => item.value === form.os_level_2);
        const osLevel2DisplayName = selectedOsLevel2?.display_name || '';

        // Try to filter by parent (check both value and display_name)
        const filtered = metadata.os_level_3.filter(item =>
          item.metadata?.parent === form.os_level_2 ||
          item.metadata?.parent_value === form.os_level_2 ||
          item.metadata?.parent === osLevel2DisplayName ||
          item.metadata?.parent_value === osLevel2DisplayName
        );

        console.log('[OS Level 3 Filter]', {
          selectedValue: form.os_level_2,
          displayName: osLevel2DisplayName,
          totalItems: metadata.os_level_3.length,
          filteredCount: filtered.length,
          sampleMetadata: metadata.os_level_3.slice(0, 3).map(i => ({ value: i.value, metadata: i.metadata }))
        });

        // ✅ FIX: Only show filtered items, don't show all if empty
        // Client requirement: L3 should ONLY show items related to selected L2
        setFilteredOsLevel3(filtered);
      } else {
        setFilteredOsLevel3(metadata.os_level_3);
      }
    } else {
      setFilteredOsLevel3([]);
    }
  }, [form.os_level_2, metadata.os_level_2, metadata.os_level_3]);

  const handleInputChange = (field: keyof RCSAProcessForm, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field: keyof RCSAProcessForm, value: string) => {
    const currentValues = form[field] as string[];
    if (currentValues.includes(value)) {
      handleInputChange(field, currentValues.filter(v => v !== value));
    } else {
      handleInputChange(field, [...currentValues, value]);
    }
  };

  const handleAutoFillFromSummaries = () => {
    if (!documentSummaries || documentSummaries.length === 0) {
      toast.error('No document summaries available. Please run document analysis first.');
      return;
    }

    // Generate process name from first document or combine key processes
    const allKeyProcesses: string[] = [];
    const allPurposes: string[] = [];
    const allActivities: string[] = [];

    documentSummaries.forEach(doc => {
      if (doc.key_points?.key_processes) {
        allKeyProcesses.push(...doc.key_points.key_processes);
      }
      if (doc.business_context?.primary_purpose) {
        allPurposes.push(doc.business_context.primary_purpose);
      }
      if (doc.business_context?.activities_covered) {
        allActivities.push(...doc.business_context.activities_covered);
      }
    });

    // Generate process name from first key process or first document name
    let processName = '';
    if (allKeyProcesses.length > 0) {
      processName = allKeyProcesses[0].length > 100
        ? allKeyProcesses[0].substring(0, 100) + '...'
        : allKeyProcesses[0];
    } else if (documentSummaries.length > 0) {
      processName = documentSummaries[0].document_name.replace(/\.[^/.]+$/, ''); // Remove file extension
    }

    // Generate description by combining executive summaries or purposes
    let processDescription = '';
    if (allPurposes.length > 0) {
      processDescription = allPurposes.join(' ');
    } else {
      // Combine executive summaries
      const summaries = documentSummaries
        .map(doc => doc.executive_summary || doc.summary)
        .filter(Boolean)
        .slice(0, 3); // Take first 3 summaries

      if (summaries.length > 0) {
        processDescription = summaries.join('\n\n');
      }
    }

    // Truncate if too long
    if (processDescription.length > 2000) {
      processDescription = processDescription.substring(0, 2000) + '...';
    }

    const aiFields: Record<string, boolean> = {};

    if (processName) {
      handleInputChange('process_name', processName);
      aiFields.process_name = true;
    }
    if (processDescription) {
      handleInputChange('process_description', processDescription);
      aiFields.process_description = true;
    }

    handleInputChange('ai_generated_fields', { ...form.ai_generated_fields, ...aiFields });
    handleInputChange('ai_confidence_score', 75); // Lower confidence since it's derived

    toast.success(`Auto-filled from ${documentSummaries.length} document${documentSummaries.length > 1 ? 's' : ''}`);
  };

  const handleSave = async (closeWizard: boolean = true) => {
    if (!currentProject) {
      toast.error('Please select a project first');
      return false;
    }

    if (!form.rau_short_name || !form.os_level_1 || !form.process_name || !form.process_description) {
      toast.error('Please fill in all required fields');
      return false;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        project_id: currentProject.id
      };

      if (editingId) {
        await api.put(`/api/rcsa-process/${editingId}`, payload);
        toast.success('Process updated successfully');
      } else {
        const response = await api.post('/api/rcsa-process', payload);
        setEditingId(response.data?.id || null);
        toast.success('Process created successfully');
      }

      await fetchProcesses();

      if (closeWizard) {
        setShowWizard(false);
        setCurrentStep(1);
        setEditingId(null);
        setForm(initialFormState);
        setTextInputs({ business_unit_control_manager: '', external_parties_listing: '' });
      }

      return true;
    } catch (error) {
      console.error('Error saving process:', error);
      toast.error('Failed to save process');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndExport = async () => {
    const saved = await handleSave(false);
    if (saved && editingId) {
      // Export only the current process
      setExporting(true);
      try {
        const response = await api.get(`/api/rcsa-process/${editingId}/export/excel`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const contentDisposition = response.headers['content-disposition'];
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `RCSA_${form.rau_short_name}-${form.row_sequence}_${new Date().toISOString().split('T')[0]}.xlsx`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Process exported successfully');
      } catch (error) {
        console.error('Error exporting process:', error);
        toast.error('Failed to export process');
      } finally {
        setExporting(false);
      }
    }
  };

  const handleEdit = (process: RCSAProcessForm) => {
    setForm(process);
    setTextInputs({
      business_unit_control_manager: process.business_unit_control_manager?.join(', ') || '',
      external_parties_listing: process.external_parties_listing?.join(', ') || ''
    });
    setEditingId(process.id || null);
    setCurrentStep(1);
    setShowWizard(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this process?')) return;

    try {
      await api.delete(`/api/rcsa-process/${id}`);
      toast.success('Process deleted successfully');
      fetchProcesses();
    } catch (error) {
      console.error('Error deleting process:', error);
      toast.error('Failed to delete process');
    }
  };

  const handleExport = async () => {
    if (!currentProject) return;

    setExporting(true);
    try {
      const response = await api.get(`/api/rcsa-process/project/${currentProject.id}/export/excel`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RCSA_Export_${currentProject.name}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Excel exported successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleNewProcess = () => {
    setForm(initialFormState);
    setTextInputs({ business_unit_control_manager: '', external_parties_listing: '' });
    setEditingId(null);
    setCurrentStep(1);
    setShowWizard(true);
  };

  const canProceedToNextStep = () => {
    if (currentStep === 1) {
      const isValid = !!(form.rau_short_name && form.os_level_1 && form.process_name && form.process_description && form.process_scope.length > 0);
      return isValid;
    }
    return true;
  };

  const filteredProcesses = processes.filter(process =>
    process.process_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    process.rau_short_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    process.process_taxonomy?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRiskColor = (score: number) => {
    if (score >= 15) return 'bg-red-100 text-red-800';
    if (score >= 10) return 'bg-orange-100 text-orange-800';
    if (score >= 5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  if (!currentProject) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
        <div className="text-center bg-white rounded-xl shadow-sm p-12 max-w-md">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-purple-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Project Selected</h2>
          <p className="text-gray-600 mb-6">Please select a project from the header to use the RCSA Process Builder.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center shadow-lg">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#221551]">Input Process Metadata</h1>
              <p className="text-gray-600 mt-1">
                Define and export RCSA process for <span className="font-medium text-purple-600">{currentProject.name}</span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => fetchProcesses()}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleNewProcess}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#221551] text-white rounded-lg hover:bg-[#2d1d6b] shadow-md hover:shadow-lg transition-all"
            >
              <Plus className="w-4 h-4" />
              New RCSA Process
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
          <div className="text-center">
            <div className="text-2xl font-bold text-[#221551]">{processes.length}</div>
            <div className="text-sm text-gray-500">Total Processes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {processes.filter(p => p.process_scope?.includes('SOX')).length}
            </div>
            <div className="text-sm text-gray-500">SOX Processes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {processes.filter(p => p.process_scope?.includes('Operational')).length}
            </div>
            <div className="text-sm text-gray-500">Operational</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {processes.filter(p => p.process_scope?.includes('Compliance')).length}
            </div>
            <div className="text-sm text-gray-500">Compliance</div>
          </div>
        </div>
      </div>

      {/* Wizard Modal */}
      {showWizard && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto py-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 my-auto">
            {/* Wizard Header with Steps */}
            <div className="border-b border-gray-200 bg-gradient-to-r from-purple-50 to-white rounded-t-xl">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    {editingId ? <Edit2 className="w-5 h-5 text-purple-600" /> : <Plus className="w-5 h-5 text-purple-600" />}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#221551]">
                      {editingId ? 'Edit RCSA Process' : 'Create New RCSA Process'}
                    </h2>
                    <p className="text-sm text-gray-500">Step {currentStep} of {WIZARD_STEPS.length}: {WIZARD_STEPS[currentStep - 1].name}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowWizard(false); setCurrentStep(1); setEditingId(null); setForm(initialFormState); setTextInputs({ business_unit_control_manager: '', external_parties_listing: '' }); }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Step Indicators */}
              <div className="flex items-center justify-between px-6 pb-4">
                {WIZARD_STEPS.map((step, index) => (
                  <div key={step.id} className="flex items-center flex-1">
                    <div className={`flex items-center gap-2 ${currentStep >= step.id ? 'text-purple-600' : 'text-gray-400'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                        currentStep > step.id ? 'bg-purple-600 text-white' :
                        currentStep === step.id ? 'bg-purple-100 text-purple-600 ring-2 ring-purple-600' :
                        'bg-gray-100 text-gray-400'
                      }`}>
                        {currentStep > step.id ? <Check className="w-4 h-4" /> : step.id}
                      </div>
                      <div className="hidden md:block">
                        <div className="text-sm font-medium">{step.name}</div>
                      </div>
                    </div>
                    {index < WIZARD_STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-4 ${currentStep > step.id ? 'bg-purple-600' : 'bg-gray-200'}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Wizard Content */}
            <div ref={wizardContentRef} className="p-6 max-h-[calc(100vh-280px)] overflow-y-auto">
              {/* Step 1: Define Process */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  {/* Auto-Fill from Document Summaries */}
                  {documentSummaries.length > 0 && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <FileText className="w-6 h-6 text-green-600 mt-0.5" />
                          <div>
                            <p className="text-sm text-green-800 font-medium">Auto-Fill from Document Summaries</p>
                            <p className="text-sm text-green-700">
                              {documentSummaries.length} analyzed document{documentSummaries.length !== 1 ? 's' : ''} available - click to populate process name & description
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={handleAutoFillFromSummaries}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm whitespace-nowrap"
                        >
                          <Sparkles className="w-4 h-4" />
                          Auto-Fill from Summaries
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Basic Identification */}
                  <div className="bg-gray-50 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-[#221551] mb-4 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Basic Identification
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          RAU Short Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.rau_short_name}
                          onChange={e => handleInputChange('rau_short_name', e.target.value.toUpperCase())}
                          placeholder="e.g., GCM"
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          maxLength={10}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Process ID</label>
                        <input
                          type="text"
                          value={form.rau_short_name ? `${form.rau_short_name}-${form.row_sequence}` : ''}
                          disabled
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Organizational Structure */}
                  <div className="bg-gray-50 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-[#221551] mb-4 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Organizational Structure
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          OS Level 1 <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={form.os_level_1}
                          onChange={e => {
                            handleInputChange('os_level_1', e.target.value);
                            handleInputChange('os_level_2', '');
                            handleInputChange('os_level_3', '');
                          }}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Select OS Level 1</option>
                          {(metadata.os_level_1 || []).map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.display_name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">OS Level 2</label>
                        <select
                          value={form.os_level_2}
                          onChange={e => {
                            handleInputChange('os_level_2', e.target.value);
                            handleInputChange('os_level_3', '');
                          }}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                          disabled={!form.os_level_1}
                        >
                          <option value="">
                            {!form.os_level_1
                              ? 'Select OS Level 1 first'
                              : filteredOsLevel2.length === 0
                                ? 'No Level 2 items for selected Level 1'
                                : 'Select OS Level 2'}
                          </option>
                          {filteredOsLevel2.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.display_name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">OS Level 3</label>
                        <select
                          value={form.os_level_3}
                          onChange={e => handleInputChange('os_level_3', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                          disabled={!form.os_level_2}
                        >
                          <option value="">
                            {!form.os_level_2
                              ? 'Select OS Level 2 first'
                              : filteredOsLevel3.length === 0
                                ? 'No Level 3 items for selected Level 2'
                                : 'Select OS Level 3'}
                          </option>
                          {filteredOsLevel3.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.display_name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Process Taxonomy</label>
                        <select
                          value={form.process_taxonomy}
                          onChange={e => handleInputChange('process_taxonomy', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Select Taxonomy</option>
                          {(metadata.process_taxonomy || []).map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.display_name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Process Details */}
                  <div className="bg-gray-50 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-[#221551] mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Process Details
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Process Name <span className="text-red-500">*</span>
                          {form.ai_generated_fields?.process_name && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              <Sparkles className="w-3 h-3 mr-1" />
                              AI Generated
                            </span>
                          )}
                        </label>
                        <input
                          type="text"
                          value={form.process_name}
                          onChange={e => handleInputChange('process_name', e.target.value)}
                          placeholder="Enter process name"
                          className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                            form.ai_generated_fields?.process_name ? 'border-purple-300 bg-purple-50' : 'border-gray-300'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Process Description <span className="text-red-500">*</span>
                          {form.ai_generated_fields?.process_description && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              <Sparkles className="w-3 h-3 mr-1" />
                              AI Generated
                            </span>
                          )}
                        </label>
                        <textarea
                          value={form.process_description}
                          onChange={e => handleInputChange('process_description', e.target.value)}
                          placeholder="Describe the process..."
                          rows={3}
                          className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                            form.ai_generated_fields?.process_description ? 'border-purple-300 bg-purple-50' : 'border-gray-300'
                          }`}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Process Starting Point</label>
                          <input
                            type="text"
                            value={form.process_starting_point}
                            onChange={e => handleInputChange('process_starting_point', e.target.value)}
                            placeholder="What triggers this process?"
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Process Ending Point</label>
                          <input
                            type="text"
                            value={form.process_ending_point}
                            onChange={e => handleInputChange('process_ending_point', e.target.value)}
                            placeholder="What marks process completion?"
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Process Scope <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-wrap gap-3">
                          {['SOX', 'Operational', 'Compliance'].map(scope => (
                            <label
                              key={scope}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                                form.process_scope.includes(scope)
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={form.process_scope.includes(scope)}
                                onChange={() => handleMultiSelect('process_scope', scope)}
                                className="sr-only"
                              />
                              {form.process_scope.includes(scope) && (
                                <CheckCircle2 className="w-4 h-4 text-purple-600" />
                              )}
                              <span className="text-sm font-medium">{scope}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Advanced Fields Toggle */}
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center gap-2 text-[#221551] hover:text-purple-700 font-medium w-full justify-center py-3 border border-dashed border-purple-300 rounded-lg hover:bg-purple-50 transition-all"
                  >
                    {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    {showAdvanced ? 'Hide' : 'Show'} Advanced Fields
                  </button>

                  {showAdvanced && (
                    <div className="space-y-6">
                      {/* Ownership & Geography */}
                      <div className="bg-gray-50 rounded-lg p-5">
                        <h3 className="text-sm font-semibold text-[#221551] mb-4 flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          Ownership & Geography
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Process Owner</label>
                            <input
                              type="email"
                              value={form.process_owner}
                              onChange={e => handleInputChange('process_owner', e.target.value)}
                              placeholder="owner@company.com"
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Business Unit Control Manager</label>
                            <input
                              type="text"
                              value={textInputs.business_unit_control_manager}
                              onChange={e => setTextInputs(prev => ({ ...prev, business_unit_control_manager: e.target.value }))}
                              onBlur={e => handleInputChange('business_unit_control_manager', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                              placeholder="Enter emails, comma-separated"
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div>
                            <SearchableMultiSelect
                              label="Performing Country"
                              options={metadata.performing_country || []}
                              value={form.performing_country}
                              onChange={(val) => handleInputChange('performing_country', val)}
                              placeholder="Search and select countries..."
                            />
                          </div>
                          <div>
                            <SearchableMultiSelect
                              label="Performing Legal Entity"
                              options={metadata.performing_legal_entity || []}
                              value={form.performing_legal_entity}
                              onChange={(val) => handleInputChange('performing_legal_entity', val)}
                              placeholder="Search and select legal entities..."
                            />
                          </div>
                          <div>
                            <SearchableMultiSelect
                              label="Affected Countries"
                              options={metadata.affected_country || []}
                              value={form.affected_countries}
                              onChange={(val) => handleInputChange('affected_countries', val)}
                              placeholder="Search and select countries..."
                            />
                          </div>
                          <div>
                            <SearchableMultiSelect
                              label="Affected Legal Entities"
                              options={metadata.affected_legal_entity || []}
                              value={form.affected_legal_entities}
                              onChange={(val) => handleInputChange('affected_legal_entities', val)}
                              placeholder="Search and select legal entities..."
                            />
                          </div>
                        </div>
                      </div>

                      {/* Systems & Applications */}
                      <div className="bg-gray-50 rounded-lg p-5">
                        <h3 className="text-sm font-semibold text-[#221551] mb-4 flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          Systems & Applications
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <SearchableMultiSelect
                              label="Supporting Applications"
                              options={metadata.applications || []}
                              value={form.supporting_applications}
                              onChange={(val) => handleInputChange('supporting_applications', val)}
                              placeholder="Search and select applications..."
                            />
                          </div>
                          <div>
                            <SearchableMultiSelect
                              label="Supporting Models"
                              options={metadata.supporting_models || []}
                              value={form.supporting_models}
                              onChange={(val) => handleInputChange('supporting_models', val)}
                              placeholder="Search and select models..."
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Non-Model Tools</label>
                            <input
                              type="text"
                              value={form.non_model_tools}
                              onChange={e => handleInputChange('non_model_tools', e.target.value)}
                              placeholder="e.g., Excel spreadsheets, custom scripts"
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Third Party & External */}
                      <div className="bg-gray-50 rounded-lg p-5">
                        <h3 className="text-sm font-semibold text-[#221551] mb-4 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Third Party & External
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">External Third Party Support</label>
                            <select
                              value={form.external_third_party_support}
                              onChange={e => handleInputChange('external_third_party_support', e.target.value)}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="None">None</option>
                              <option value="Partial">Partial</option>
                              <option value="All">All</option>
                            </select>
                          </div>
                          <div>
                            <SearchableMultiSelect
                              label="Third Party Relationship Type"
                              options={metadata.third_party_relationship_type || []}
                              value={form.third_party_relationship_type}
                              onChange={(val) => handleInputChange('third_party_relationship_type', val)}
                              placeholder="Search and select relationship types..."
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">External Parties Listing</label>
                            <input
                              type="text"
                              value={textInputs.external_parties_listing}
                              onChange={e => setTextInputs(prev => ({ ...prev, external_parties_listing: e.target.value }))}
                              onBlur={e => handleInputChange('external_parties_listing', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                              placeholder="Enter third party names, comma-separated"
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Products & Business Functions */}
                      <div className="bg-gray-50 rounded-lg p-5">
                        <h3 className="text-sm font-semibold text-[#221551] mb-4 flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Products & Business Functions
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <SearchableMultiSelect
                              label="Related Products"
                              options={metadata.product_inventory || []}
                              value={form.related_products}
                              onChange={(val) => handleInputChange('related_products', val)}
                              placeholder="Search and select products..."
                            />
                          </div>
                          <div>
                            <SearchableMultiSelect
                              label="Related Business Functions"
                              options={metadata.business_functions || []}
                              value={form.related_bias}
                              onChange={(val) => handleInputChange('related_bias', val)}
                              placeholder="Search and select business functions..."
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Alternative Practice ID</label>
                            <input
                              type="text"
                              value={form.alternative_practice_id}
                              onChange={e => handleInputChange('alternative_practice_id', e.target.value)}
                              placeholder="e.g., ALT-001"
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Save */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  {/* Completion Status Banner */}
                  {(processSteps.length === 0 || riskAssessments.length === 0) ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm text-amber-800 font-semibold mb-2">⚠️ Incomplete Data Detected</p>
                          <p className="text-sm text-amber-700 mb-3">
                            Complete these sections for comprehensive data:
                          </p>
                          <div className="space-y-2">
                            {processSteps.length === 0 && (
                              <div className="flex items-center gap-2 text-sm text-amber-800">
                                <span className="font-medium">• Process Steps: {processSteps.length}</span>
                                <span className="text-amber-600">→</span>
                                <a
                                  href={`/projects/${currentProject?.id}/process-maps`}
                                  className="text-amber-700 hover:text-amber-900 underline font-medium"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    window.location.href = `/projects/${currentProject?.id}/process-maps`;
                                  }}
                                >
                                  Go to Process Maps
                                </a>
                              </div>
                            )}
                            {riskAssessments.length === 0 && (
                              <div className="flex items-center gap-2 text-sm text-amber-800">
                                <span className="font-medium">• Risk Assessments: {riskAssessments.length}</span>
                                <span className="text-amber-600">→</span>
                                <a
                                  href={`/projects/${currentProject?.id}/risk-mapping`}
                                  className="text-amber-700 hover:text-amber-900 underline font-medium"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    window.location.href = `/projects/${currentProject?.id}/risk-mapping`;
                                  }}
                                >
                                  Go to Risk Assessment
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-green-800 font-medium">✅ Ready to Save - All Data Complete!</p>
                          <p className="text-sm text-green-700">Review your RCSA process details below and save.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <ClipboardList className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Process</div>
                          <div className="font-semibold text-gray-900">{form.process_name || 'Not defined'}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        ID: {form.rau_short_name}-{form.row_sequence}
                      </div>
                    </div>

                    <div className={`bg-white border rounded-lg p-4 ${processSteps.length === 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${processSteps.length === 0 ? 'bg-amber-100' : 'bg-blue-100'}`}>
                          <GitBranch className={`w-5 h-5 ${processSteps.length === 0 ? 'text-amber-600' : 'text-blue-600'}`} />
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Process Steps</div>
                          <div className={`font-semibold ${processSteps.length === 0 ? 'text-amber-700' : 'text-gray-900'}`}>
                            {processSteps.length} steps
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        From Process Maps
                      </div>
                    </div>

                    <div className={`bg-white border rounded-lg p-4 ${riskAssessments.length === 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${riskAssessments.length === 0 ? 'bg-amber-100' : 'bg-orange-100'}`}>
                          <Shield className={`w-5 h-5 ${riskAssessments.length === 0 ? 'text-amber-600' : 'text-orange-600'}`} />
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Risk Assessments</div>
                          <div className={`font-semibold ${riskAssessments.length === 0 ? 'text-amber-700' : 'text-gray-900'}`}>
                            {riskAssessments.length} risks
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        From Risk Mapping
                      </div>
                    </div>
                  </div>

                  {/* Process Details Review */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-[#221551] mb-4">Process Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div className="text-sm text-gray-500">Organization</div>
                        <div className="text-gray-900">{form.os_level_1} &gt; {form.os_level_2 || 'N/A'} &gt; {form.os_level_3 || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Taxonomy</div>
                        <div className="text-gray-900">{form.process_taxonomy || 'Not specified'}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Scope</div>
                        <div className="flex gap-2 mt-1">
                          {form.process_scope.map(scope => (
                            <span key={scope} className="inline-flex px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">
                              {scope}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Process Owner</div>
                        <div className="text-gray-900">{form.process_owner || 'Not specified'}</div>
                      </div>
                      <div className="md:col-span-2">
                        <div className="text-sm text-gray-500">Description</div>
                        <div className="text-gray-900">{form.process_description}</div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                      onClick={() => handleSave(true)}
                      disabled={saving}
                      className="flex items-center justify-center gap-3 px-8 py-4 bg-[#221551] text-white rounded-xl hover:bg-[#2d1d6b] shadow-lg hover:shadow-xl transition-all text-lg font-semibold"
                    >
                      {saving ? <Loader2 className="w-6 h-6 animate-spin" /> : <Save className="w-6 h-6" />}
                      {saving ? 'Saving...' : 'Save Process'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Wizard Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : setShowWizard(false)}
                className="flex items-center gap-2 px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4" />
                {currentStep === 1 ? 'Cancel' : 'Previous'}
              </button>

              <div className="flex items-center gap-3">
                {currentStep < 2 ? (
                  <button
                    onClick={() => setCurrentStep(currentStep + 1)}
                    disabled={!canProceedToNextStep()}
                    className="flex items-center gap-2 px-6 py-2.5 bg-[#221551] text-white rounded-lg hover:bg-[#2d1d6b] disabled:opacity-50 shadow-md"
                  >
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => handleSave(true)}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 shadow-md"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save & Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      {!showWizard && processes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by process name, RAU, or taxonomy..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Filter className="w-4 h-4" />
              {filteredProcesses.length} of {processes.length} processes
            </div>
          </div>
        </div>
      )}

      {/* Processes List */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="w-12 h-12 animate-spin text-purple-600 mb-4" />
            <p className="text-gray-600">Loading RCSA processes...</p>
          </div>
        </div>
      ) : processes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
          <div className="text-center max-w-md mx-auto">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ClipboardList className="w-10 h-10 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No RCSA Processes Yet</h3>
            <p className="text-gray-600 mb-6">
              Get started by creating your first RCSA process definition.
            </p>
            <button
              onClick={handleNewProcess}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#221551] text-white rounded-lg hover:bg-[#2d1d6b] shadow-md"
            >
              <Plus className="w-5 h-5" />
              Create First Process
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#221551] to-[#3d2d7a] text-white">
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Process ID</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Process Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Taxonomy</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase">OS Level 1</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Scope</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredProcesses.map((process, index) => (
                  <React.Fragment key={process.id}>
                    <tr className={`hover:bg-purple-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-mono font-semibold bg-purple-100 text-purple-800">
                          {process.rau_short_name}-{process.row_sequence}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">{process.process_name}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{process.process_taxonomy || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{process.os_level_1 || '-'}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {process.process_scope?.map(scope => (
                            <span
                              key={scope}
                              className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                scope === 'SOX' ? 'bg-green-100 text-green-800' :
                                scope === 'Operational' ? 'bg-blue-100 text-blue-800' :
                                'bg-orange-100 text-orange-800'
                              }`}
                            >
                              {scope}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setExpandedRow(expandedRow === process.id ? null : process.id!)}
                            className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(process)}
                            className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => process.id && handleDelete(process.id)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRow === process.id && (
                      <tr className="bg-purple-50">
                        <td colSpan={6} className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Process Details</h4>
                              <div className="space-y-1 text-sm">
                                <div><span className="text-gray-500">Starting Point:</span> {process.process_starting_point || 'Not specified'}</div>
                                <div><span className="text-gray-500">Ending Point:</span> {process.process_ending_point || 'Not specified'}</div>
                                <div><span className="text-gray-500">Owner:</span> {process.process_owner || 'Not specified'}</div>
                              </div>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Organization</h4>
                              <div className="space-y-1 text-sm">
                                <div><span className="text-gray-500">OS Level 2:</span> {process.os_level_2 || '-'}</div>
                                <div><span className="text-gray-500">OS Level 3:</span> {process.os_level_3 || '-'}</div>
                              </div>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Systems</h4>
                              <div className="space-y-1 text-sm">
                                <div><span className="text-gray-500">Third Party:</span> {process.external_third_party_support}</div>
                                <div><span className="text-gray-500">Applications:</span> {process.supporting_applications?.join(', ') || 'None'}</div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RCSAProcessBuilder;
